import { Plus, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/Button";
import { useConfirm } from "../../components/ui/ConfirmDialog";
import { EmptyState, Skeleton } from "../../components/ui/Feedback";
import { Input, Select } from "../../components/ui/Input";
import { useToast } from "../../components/ui/Toast";
import { LabelWithHint } from "../../components/ui/Tooltip";
import { majorToMinor, minorToMajor } from "../../lib/money";
import type {
	GiftDiscountKind,
	SelectorKind,
	SubscriptionOffer,
	SubscriptionOfferPatch,
	SubscriptionTrigger,
	SubscriptionTriggerKind,
} from "../../lib/types";
import { usePlansForProducts } from "../products/usePlansForProducts";
import { SellingPlanOptions } from "../products/SellingPlanOptions";
import { useProducts } from "../products/useProducts";
import { SelectorField } from "./SelectorField";
import {
	useAddTrigger,
	useCreateSubscriptionOffer,
	useDeleteSubscriptionOffer,
	useDeleteTrigger,
	useSubscriptionOffers,
	useUpdateSubscriptionOffer,
	useUpdateTrigger,
} from "./useSubscriptionOffers";

/**
 * Products and variants only. A broader selector would match products with no
 * selling plan, which the offer then skips at runtime, so the merchant would be
 * configuring something that quietly does less than it says.
 */
const OFFER_KINDS: SelectorKind[] = ["product", "variant"];

/** Conditions read the whole cart, so they can match anything in it. */
const TRIGGER_KINDS: SelectorKind[] = ["product", "variant", "product_type", "vendor", "tag"];

const SELECTOR_HELP = "Products must have a selling plan, handled by a subscription app.";

const PLAN_HELP =
	"Pin one plan and the prompt is a single yes or no. Let the shopper choose and they pick their own cadence, which is worth it only when cadence genuinely varies.";

const DISCOUNT_HELP =
	"On top of whatever the selling plan already takes off. Leave it at none and the plan's own saving is the whole offer.";

const TRIGGER_LABELS: Record<SubscriptionTriggerKind, string> = {
	subject_quantity: "They have at least this many of the item",
	cart_contains: "The cart contains",
	cart_excludes: "The cart does not contain",
	cart_subtotal: "Cart total is",
	cart_units: "Cart holds this many units",
};

const NEEDS_SELECTOR = new Set<SubscriptionTriggerKind>(["cart_contains", "cart_excludes"]);
const IS_MONEY = new Set<SubscriptionTriggerKind>(["cart_subtotal"]);

const TriggerRow = ({
	trigger,
	campaignId,
	offerId,
}: {
	trigger: SubscriptionTrigger;
	campaignId: string;
	offerId: string;
}) => {
	const update = useUpdateTrigger(campaignId);
	const remove = useDeleteTrigger(campaignId);
	const toast = useToast();
	const notify = { onError: (error: unknown) => toast.push((error as Error).message, "error") };

	const money = IS_MONEY.has(trigger.kind);
	const save = (patch: Parameters<typeof update.mutate>[0] extends infer T ? Partial<T> : never) =>
		update.mutate({ offerId, triggerId: trigger.id, ...patch }, notify);

	return (
		<div className="space-y-3 rounded-md border border-line bg-canvas p-3">
			<div className="flex items-center justify-between gap-3">
				<span className="text-xs font-medium text-ink">{TRIGGER_LABELS[trigger.kind]}</span>
				<Button
					variant="ghost"
					size="sm"
					aria-label="Remove condition"
					onClick={() => remove.mutate({ offerId, triggerId: trigger.id }, notify)}
				>
					<Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
				</Button>
			</div>

			{NEEDS_SELECTOR.has(trigger.kind) ? (
				<SelectorField
					label="Which products"
					kind={trigger.selectorKind ?? "product"}
					values={trigger.selectorValues}
					kinds={TRIGGER_KINDS}
					onChange={(next) => save({ selectorKind: next.kind, selectorValues: next.values })}
				/>
			) : null}

			<div className="grid gap-3 sm:grid-cols-2">
				<label className="block space-y-1.5">
					<span className="text-xs text-ink-muted">{money ? "At least" : "Minimum"}</span>
					<Input
						type="number"
						min="0"
						step={money ? "0.01" : "1"}
						defaultValue={
							trigger.minValue === null
								? ""
								: money
									? minorToMajor(trigger.minValue)
									: trigger.minValue
						}
						onBlur={(event) =>
							save({
								minValue: event.target.value
									? money
										? majorToMinor(event.target.value)
										: Number(event.target.value)
									: null,
							})
						}
					/>
				</label>
				<label className="block space-y-1.5">
					<span className="text-xs text-ink-muted">{money ? "At most" : "Maximum"}</span>
					<Input
						type="number"
						min="0"
						step={money ? "0.01" : "1"}
						defaultValue={
							trigger.maxValue === null
								? ""
								: money
									? minorToMajor(trigger.maxValue)
									: trigger.maxValue
						}
						onBlur={(event) =>
							save({
								maxValue: event.target.value
									? money
										? majorToMinor(event.target.value)
										: Number(event.target.value)
									: null,
							})
						}
					/>
				</label>
			</div>
		</div>
	);
};

const OfferCard = ({
	offer,
	campaignId,
	index,
}: {
	offer: SubscriptionOffer;
	campaignId: string;
	index: number;
}) => {
	const update = useUpdateSubscriptionOffer(campaignId);
	const remove = useDeleteSubscriptionOffer(campaignId);
	const addTrigger = useAddTrigger(campaignId);
	const toast = useToast();
	const confirm = useConfirm();

	const notify = { onError: (error: unknown) => toast.push((error as Error).message, "error") };
	const save = (patch: SubscriptionOfferPatch) =>
		update.mutate({ offerId: offer.id, ...patch }, notify);

	// Plans come from the products the offer targets, so the list is only real
	// once something is selected.
	const plans = usePlansForProducts(offer.selectorValues);
	const available = [
		...new Map(
			Object.values(plans.data?.plans ?? {})
				.flat()
				.map((plan) => [plan.gid, plan] as const),
		).values(),
	];

	// Everything below the selector describes what to do with a plan, so none of
	// it means anything until a product that has one is chosen. Showing it early
	// asks the merchant to configure an offer that could not run.
	const ready = offer.selectorValues.length > 0 && available.length > 0;

	return (
		<div className="space-y-4 rounded-md border border-line bg-surface p-4">
			<div className="flex items-center justify-between gap-3">
				<span className="text-xs font-medium text-ink">Offer {index + 1}</span>
				<Button
					variant="ghost"
					size="sm"
					aria-label="Delete offer"
					onClick={() =>
						confirm({
							title: "Delete this offer?",
							description: "It stops being shown after the next publish.",
							onConfirm: () =>
								remove.mutate(offer.id, {
									onSuccess: () => toast.push("Offer deleted"),
									...notify,
								}),
						})
					}
				>
					<Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
				</Button>
			</div>

			<SelectorField
				label="Offer it on"
				kind={offer.selectorKind}
				values={offer.selectorValues}
				kinds={OFFER_KINDS}
				subscribableOnly
				help={SELECTOR_HELP}
				onChange={(next) => save({ selectorKind: next.kind, selectorValues: next.values })}
			/>

			{plans.isPending && offer.selectorValues.length > 0 ? (
				<Skeleton className="h-24 w-full" />
			) : !ready ? null : (
				<>
					<div className="grid gap-4 border-t border-line pt-4 sm:grid-cols-2">
						<label className="block space-y-1.5">
							<LabelWithHint label="Plan">{PLAN_HELP}</LabelWithHint>
							<Select
								value={offer.planMode}
								onChange={(event) =>
									save({ planMode: event.target.value as "pinned", sellingPlanGid: null })
								}
							>
								<option value="pinned">Pin one plan</option>
								<option value="choose">Shopper picks the plan</option>
							</Select>
						</label>

						{offer.planMode === "pinned" ? (
							<label className="block space-y-1.5">
								<span className="text-sm text-ink">Which plan</span>
								<Select
									value={offer.sellingPlanGid ?? ""}
									onChange={(event) => save({ sellingPlanGid: event.target.value || null })}
								>
									<option value="">Choose a plan</option>
									<SellingPlanOptions plans={available} />
								</Select>
							</label>
						) : null}
					</div>

					<div className="grid gap-4 border-t border-line pt-4 sm:grid-cols-2">
						<label className="block space-y-1.5">
							<LabelWithHint label="Extra discount">{DISCOUNT_HELP}</LabelWithHint>
							<Select
								value={offer.discountKind ?? ""}
								onChange={(event) =>
									save({
										discountKind: (event.target.value || null) as GiftDiscountKind | null,
										discountValue: 0,
									})
								}
							>
								<option value="">None, the plan's own saving</option>
								<option value="percentage">Percentage off</option>
								<option value="fixed_amount">Amount off</option>
							</Select>
						</label>

						{offer.discountKind ? (
							<label className="block space-y-1.5">
								<span className="text-sm text-ink">
									{offer.discountKind === "percentage" ? "Percent off" : "Amount off"}
								</span>
								<Input
									type="number"
									min="0"
									max={offer.discountKind === "percentage" ? "100" : undefined}
									step={offer.discountKind === "percentage" ? "1" : "0.01"}
									defaultValue={
										offer.discountKind === "percentage"
											? String(offer.discountValue / 100)
											: minorToMajor(offer.discountValue)
									}
									onBlur={(event) =>
										save({
											discountValue:
												offer.discountKind === "percentage"
													? Math.round(Number(event.target.value || "0") * 100)
													: majorToMinor(event.target.value || "0"),
										})
									}
								/>
							</label>
						) : null}
					</div>

					<div className="space-y-3 border-t border-line pt-4">
						<label className="block space-y-1.5">
							<span className="text-sm text-ink">Headline</span>
							<Input
								defaultValue={offer.headline ?? ""}
								placeholder="Subscribe and never run out"
								onBlur={(event) => save({ headline: event.target.value })}
							/>
						</label>
						<label className="block space-y-1.5">
							<span className="text-sm text-ink">Supporting line</span>
							<Input
								defaultValue={offer.body ?? ""}
								placeholder="Delivered monthly, cancel whenever you like"
								onBlur={(event) => save({ body: event.target.value })}
							/>
						</label>
					</div>

					<div className="space-y-3 border-t border-line pt-4">
						<div className="flex flex-wrap items-center justify-between gap-2">
							<LabelWithHint label="Only when">
								Conditions on the whole cart. All of them have to hold, so a shopper who fails one
								is not asked. Add none and the offer shows on every cart holding a matching product.
							</LabelWithHint>
							<Select
								className="h-8 w-auto"
								value=""
								onChange={(event) => {
									if (!event.target.value) return;
									addTrigger.mutate(
										{
											offerId: offer.id,
											kind: event.target.value as SubscriptionTriggerKind,
											selectorKind: NEEDS_SELECTOR.has(
												event.target.value as SubscriptionTriggerKind,
											)
												? "product"
												: null,
										},
										notify,
									);
									event.target.value = "";
								}}
							>
								<option value="">Add a condition…</option>
								{(Object.keys(TRIGGER_LABELS) as SubscriptionTriggerKind[]).map((kind) => (
									<option key={kind} value={kind}>
										{TRIGGER_LABELS[kind]}
									</option>
								))}
							</Select>
						</div>

						{offer.triggers.map((trigger) => (
							<TriggerRow
								key={trigger.id}
								trigger={trigger}
								campaignId={campaignId}
								offerId={offer.id}
							/>
						))}
					</div>
				</>
			)}
		</div>
	);
};

export const SubscriptionEditor = ({ campaignId }: { campaignId: string }) => {
	const offers = useSubscriptionOffers(campaignId);
	const create = useCreateSubscriptionOffer(campaignId);
	const toast = useToast();

	// One row is enough to know whether the shop has anything to offer at all.
	const subscribable = useProducts({
		search: "",
		status: "",
		limit: 1,
		offset: 0,
		subscribable: true,
	});
	const noPlans = subscribable.data?.anySellingPlan === false;

	const items = offers.data?.items ?? [];

	return (
		<div className="space-y-4">
			{noPlans ? (
				<div className="space-y-1 rounded-md border border-alert-line bg-alert-soft px-4 py-3">
					<p className="text-sm font-medium text-alert">
						No products on this store are sold on a subscription
					</p>
					<p className="text-xs text-ink-muted">
						This offer moves a cart line onto a selling plan, so there is nothing for it to do yet.
						Add a selling plan to a product in Shopify, then run a subscription sync from{" "}
						<Link to="/settings" className="underline underline-offset-4">
							Settings
						</Link>
						.
					</p>
				</div>
			) : null}

			<div className="flex flex-wrap items-center justify-between gap-3">
				<p className="max-w-md text-xs text-ink-muted">
					When a shopper adds one of these products without a subscription, they are asked once
					whether they would rather subscribe. Switching happens in the cart, before checkout.
				</p>
				<Button
					variant="outline"
					size="sm"
					disabled={create.isPending}
					onClick={() =>
						create.mutate(
							{ selectorKind: "product", selectorValues: [], planMode: "pinned" },
							{
								onSuccess: () => toast.push("Offer added"),
								onError: (error) => toast.push(error.message, "error"),
							},
						)
					}
				>
					<Plus className="h-3.5 w-3.5" strokeWidth={2} />
					Add offer
				</Button>
			</div>

			{offers.isPending ? <Skeleton className="h-40 w-full" /> : null}

			{offers.data && items.length === 0 ? (
				<EmptyState
					title="No offers yet"
					description="Add one to start asking shoppers whether they would rather subscribe."
				/>
			) : null}

			{items.map((offer, index) => (
				<OfferCard key={offer.id} offer={offer} campaignId={campaignId} index={index} />
			))}
		</div>
	);
};
